import { FlowButton } from 'components/ui/flow-button';
import { useEffect, useState } from 'react';
import { Loader2, LockKeyhole, LogOut, Mail, ShieldCheck } from 'lucide-react';
import NotFoundPage from 'pages/NotFoundPage';
import { isCustomerIdentityPreviewEnabled } from 'lib/customerIdentityConfig';
import { signInCustomer, signOutCustomer, watchCustomerAuth } from 'lib/customerAuth';
import { loadCustomerIdentity } from 'lib/customerApi';

function CustomerIdentityPreview() {
  const [authState, setAuthState] = useState({ status: 'loading', user: null, error: null });
  const [profileState, setProfileState] = useState({ status: 'idle', customer: null, error: null });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    let unsubscribe = null;
    watchCustomerAuth((nextState) => {
      if (active) setAuthState({ error: null, ...nextState });
    }).then((stop) => {
      if (!active) stop();
      else unsubscribe = stop;
    }).catch((error) => {
      if (active) setAuthState({ status: 'error', user: null, error });
    });
    return () => {
      active = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (authState.status !== 'verified' || !authState.user) {
      setProfileState({ status: 'idle', customer: null, error: null });
      return () => { active = false; };
    }

    setProfileState({ status: 'loading', customer: null, error: null });
    loadCustomerIdentity(authState.user).then((result) => {
      if (active) setProfileState({ status: 'ready', customer: result.customer, error: null });
    }).catch((error) => {
      if (active) setProfileState({ status: 'error', customer: null, error });
    });
    return () => { active = false; };
  }, [authState.status, authState.user]);

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setAuthState((current) => ({ ...current, error: null }));
    try {
      await signInCustomer(email, password);
      setPassword('');
    } catch (error) {
      setAuthState({ status: 'signed_out', user: null, error });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignOut() {
    setSubmitting(true);
    try {
      await signOutCustomer();
    } finally {
      setSubmitting(false);
    }
  }

  const signedIn = authState.status === 'verified' && authState.user;
  const error = authState.error || profileState.error;

  return (
    <main className="min-h-screen bg-obsidian px-6 pb-20 pt-32 text-white">
      <section className="mx-auto max-w-5xl">
        <div className="border border-gold/20 bg-obsidian-surface shadow-[0_30px_100px_rgba(0,0,0,0.45)]">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
            <div className="border-b border-white/10 p-8 md:p-12 lg:border-b-0 lg:border-r">
              <div className="mb-8 inline-flex items-center gap-2 border border-gold/30 bg-gold/5 px-3 py-2 font-body text-xs uppercase tracking-[0.22em] text-gold">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                Private identity preview
              </div>
              <h1 className="font-display text-4xl font-semibold leading-tight md:text-5xl">
                One verified customer identity.
              </h1>
              <p className="mt-6 max-w-xl font-body text-base leading-7 text-white/65">
                This isolated preview tests the future Magellan website account foundation. Your verified email is the visible username; ownership will rely on an immutable server-verified UID.
              </p>
              <div className="mt-10 space-y-4 font-body text-sm leading-6 text-white/60">
                <div className="flex gap-3">
                  <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden="true" />
                  <p>Authentication and authorization are checked by the website server.</p>
                </div>
                <div className="flex gap-3">
                  <Mail className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden="true" />
                  <p>Email verification is required, but email alone never proves a purchase or company relationship.</p>
                </div>
              </div>
              <p className="mt-10 border-l-2 border-gold/50 pl-4 font-body text-xs leading-5 text-white/45">
                This preview does not change checkout, payments, paperwork, downloads, training, agreements, DriverReach access, or TMS permissions.
              </p>
            </div>

            <div className="p-8 md:p-12">
              {authState.status === 'loading' ? (
                <div className="flex min-h-64 items-center justify-center" role="status">
                  <Loader2 className="h-7 w-7 animate-spin text-gold" aria-hidden="true" />
                  <span className="sr-only">Loading customer sign-in</span>
                </div>
              ) : signedIn ? (
                <div>
                  <p className="font-body text-xs uppercase tracking-[0.22em] text-gold">Verified session</p>
                  <h2 className="mt-3 font-display text-3xl font-semibold">Customer identity</h2>
                  {profileState.status === 'loading' && (
                    <div className="mt-8 flex items-center gap-3 font-body text-sm text-white/60" role="status">
                      <Loader2 className="h-5 w-5 animate-spin text-gold" aria-hidden="true" />
                      Loading the server-owned profile…
                    </div>
                  )}
                  {profileState.status === 'ready' && profileState.customer && (
                    <dl className="mt-8 space-y-5 font-body">
                      <div>
                        <dt className="text-xs uppercase tracking-[0.18em] text-white/40">Username</dt>
                        <dd className="mt-1 break-all text-base text-white">{profileState.customer.email}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-[0.18em] text-white/40">Email</dt>
                        <dd className="mt-1 text-base text-emerald-300">Verified</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-[0.18em] text-white/40">Account state</dt>
                        <dd className="mt-1 text-base text-white">{profileState.customer.accountState}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-[0.18em] text-white/40">Legacy access claim</dt>
                        <dd className="mt-1 text-base text-white">{profileState.customer.legacyLinkage?.status || 'NOT_EVALUATED'}</dd>
                      </div>
                    </dl>
                  )}
                  <button
                    type="button"
                    onClick={handleSignOut}
                    disabled={submitting}
                    className="mt-10 inline-flex min-h-12 items-center justify-center gap-2 border border-white/20 px-5 font-body text-sm text-white transition hover:border-gold/60 hover:text-gold disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <LogOut className="h-4 w-4" aria-hidden="true" />
                    Sign out
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <p className="font-body text-xs uppercase tracking-[0.22em] text-gold">Existing verified accounts only</p>
                  <h2 className="mt-3 font-display text-3xl font-semibold">Sign in</h2>
                  <div className="mt-8 space-y-5">
                    <label className="block font-body text-sm text-white/75">
                      Email address
                      <input
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        required
                        className="mt-2 min-h-12 w-full border border-white/15 bg-black/30 px-4 text-white outline-none transition focus:border-gold/70"
                      />
                    </label>
                    <label className="block font-body text-sm text-white/75">
                      Password
                      <input
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        required
                        className="mt-2 min-h-12 w-full border border-white/15 bg-black/30 px-4 text-white outline-none transition focus:border-gold/70"
                      />
                    </label>
                  </div>
                  <FlowButton
                    type="submit"
                    disabled={submitting}
                    className="mt-7 inline-flex min-h-12 w-full items-center justify-center gap-2  px-5 font-body text-sm font-semibold  transition  disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                    Verify account
                  </FlowButton>
                </form>
              )}

              {error && (
                <p className="mt-6 border border-red-400/30 bg-red-400/10 px-4 py-3 font-body text-sm leading-6 text-red-100" role="alert">
                  {error.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default function CustomerIdentityPreviewPage() {
  if (!isCustomerIdentityPreviewEnabled()) return <NotFoundPage />;
  return <CustomerIdentityPreview />;
}
